import type { ReactNode } from "react";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { WIP_ACTION_HINT, WipHint } from "@/components/ui/wip-hint";
import { cn } from "@/lib/utils";

/**
 * Shared WIP affordances for the three recovery routes ported in ALL-5825
 * (`/recovery/execution`, `/recovery/backup`, `/recovery/audit`).
 *
 * No recovery backend exists in this environment, and every one of these is a
 * custody-adjacent ceremony (key backup / recovery), so the rule is the same
 * on all three: the designer's layout is ported faithfully, but no record,
 * count, status or hash is rendered, lists show a designed empty state, and
 * every control that would need the backend is `disabled` and described by
 * `WIP_ACTION_HINT` — never a fake handler, and never a toast claiming a
 * success that did not happen.
 *
 * These are the parts all three pages share, extracted here rather than
 * triplicated so the WIP language and the disabled-control contract cannot
 * drift between sibling screens.
 */

/** Id of the single visually-hidden node describing this page's inert controls. */
export const RECOVERY_WIP_DESCRIPTION_ID = "recovery-wip-action-hint";

/**
 * Props for a control that needs the missing backend. Spread onto a `<button>`
 * or `<Button>`; `title` is the mouse affordance and `aria-describedby` the
 * screen-reader one, both reading the same shared sentence.
 */
export function wipActionProps(): {
  disabled: true;
  title: string;
  "aria-describedby": string;
} {
  return { disabled: true, title: WIP_ACTION_HINT, "aria-describedby": RECOVERY_WIP_DESCRIPTION_ID };
}

/** The `aria-describedby` target for `wipActionProps()`; render exactly once per page. */
export function WipActionDescription() {
  return (
    <span id={RECOVERY_WIP_DESCRIPTION_ID} className="sr-only">
      {WIP_ACTION_HINT}
    </span>
  );
}

/**
 * Designer page head for a recovery route: the Recovery breadcrumb, the title
 * with the WIP chip beside it, one plain sentence saying there is no backend
 * here yet, and the page's own controls (tabs or action buttons) on the right.
 */
export function RecoveryWipHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description: string;
  actions?: ReactNode;
}) {
  return (
    <>
      <Breadcrumb items={[{ label: "Recovery", href: "/recovery" }, { label: title }]} />
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-extrabold text-ink">{title}</h1>
            <WipHint />
          </div>
          <p className="mt-1 text-sm text-muted">{description}</p>
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2.5">{actions}</div> : null}
      </div>
    </>
  );
}

/**
 * The designer's state switcher (the execution and backup screens each carry
 * one). A switcher selects between records that exist; since no record exists
 * here, every trigger is inert — rendered in the designer's own classes so the
 * layout matches, disabled with the shared reason. `active` only marks which
 * trigger the designer shows selected; nothing is selectable.
 */
export function RecoveryWipTabs({ label, tabs, active }: { label: string; tabs: string[]; active: string }) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-1.5">
      {tabs.map((tab) => (
        <button
          key={tab}
          type="button"
          {...wipActionProps()}
          className={cn(
            "rounded-[6px] border px-3 py-1.5 text-xs font-semibold",
            tab === active ? "border-primary bg-primary text-on-primary" : "border-line bg-surface text-muted",
            "disabled:cursor-not-allowed disabled:opacity-60",
          )}
        >
          {tab}
        </button>
      ))}
    </div>
  );
}
